import { useMemberFeatures } from "@/api/me";
import { useRequestASupplierMutation } from "@/api/request";
import { Button } from "@/components/ui/Button";
import { ButtonLink, IconButtonLink } from "@/components/ui/Link";
import { GivenwellLogo } from "@/icons/givenwell";
import { captureCustomEvent } from "@/utils/posthog";
import {
  Box,
  Description,
  Field,
  Flex,
  Form,
  Heading,
  Img,
  Input,
  Label,
  Span,
  Text,
  Textarea,
} from "@givenwell/components";
import { ChevronLeftIcon } from "@givenwell/core-solid-icons";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { useLayoutEffect, useState } from "react";
import { email, fallback, object, optional, pipe, string } from "valibot";
import laurel from "./laurel.svg";
import smartphoneShopping2 from "./smartphone-shopping-2.png";
import whosAwesome from "./whos-awesome.gif";

export const Route = createFileRoute("/nominate")({
  validateSearch: object({
    utm: fallback(optional(string()), undefined),
    from: fallback(optional(string()), undefined),
    email: fallback(optional(pipe(string(), email())), undefined),
  }),
  component: Page,
});

const LANDSCAPE = "@media (min-width: 840px) and (orientation: landscape)";

const route = getRouteApi("/nominate");

function Page() {
  useLayoutEffect(() => {
    document.body.style.backgroundColor = colors.warmGrey;
    return () => {
      document.body.style.removeProperty("background-color");
    };
  }, []);

  const { utm, from, email: initialEmail } = route.useSearch();

  const [details, setDetails] = useState("");
  const [email, setEmail] = useState(initialEmail?.trim() || "");
  const showEmail = !(initialEmail?.trim() || false);

  const mut = useRequestASupplierMutation();
  const features = useMemberFeatures();
  const showEapWording = features.eapSupporter && !features.hasAllowanceMarketplace;

  return (
    <Box css={{ px: 20, minH: "100dvh", display: "flex", flexDirection: "column", items: "center", width: "100%" }}>
      <Img
        src={smartphoneShopping2}
        css={{
          position: "fixed",
          zIndex: -1,
          bottom: "10vmin",
          left: 0,
          width: "min(50vmin, 650px)",
          opacity: 0.15,
          transform: "translate(-50%, 50%) rotate(15deg)",
        }}
      />
      <Img
        src={smartphoneShopping2}
        css={{
          position: "fixed",
          zIndex: -1,
          opacity: 0.15,
          top: "5vmin",
          right: "-10vmin",
          width: "min(50vmin, 650px)",
          transform: "translate(50%, -50%) rotate(-30deg)",
        }}
      />
      <Flex
        css={{
          pt: 32,
          pb: 48,
          justify: "flex-start",
          width: "100%",
          [LANDSCAPE]: {
            justify: "center",
          },
        }}
      >
        {from && (
          <IconButtonLink
            to={from as any}
            variant="plain"
            color="neutral"
            aria-label="Back"
            css={{
              mr: 32,
              [LANDSCAPE]: {
                position: "absolute",
                top: "max(20px, var(--safe-area-inset-top))",
                left: "max(20px, var(--safe-area-inset-left))",
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButtonLink>
        )}
        <GivenwellLogo
          css={{
            height: 32,
            width: 124,
          }}
        />
      </Flex>
      {mut.isSuccess ? (
        <>
          <Flex
            css={{
              flexDirection: "column",
              gap: 48,
              maxW: "400px",
              textAlign: "center",
            }}
          >
            {showEapWording ? null : (
              <Img
                src={whosAwesome}
                css={{
                  mx: "auto",
                  // maxW: 200,
                  borderRadius: 6,
                  [LANDSCAPE]: {
                    maxW: 260,
                  },
                }}
              />
            )}
            <Box css={{ spaceY: 12 }}>
              {showEapWording ? null : (
                <Heading
                  css={{
                    fontScale: "3xl",
                    lineHeight: 1.1,
                    weight: 500,
                    color: colors.text,
                    [LANDSCAPE]: { fontScale: "4xl" },
                  }}
                >
                  You’re Awesome
                </Heading>
              )}
              <Text css={{ color: colors.text2, fontScale: "lg", mt: 16 }}>
                Thank you for nominating!
                <br />
                Our <Span css={{ whiteSpace: "nowrap" }}>team will follow up soon.</Span>
              </Text>
              <Text css={{ color: colors.text2, fontScale: "lg" }}>– The Team at Givenwell ❤️</Text>
              {from && (
                <ButtonLink
                  leftIcon={<ChevronLeftIcon width={12} height={12} />}
                  size="md"
                  css={{ mt: 24 }}
                  variant="filled"
                  to={from as any}
                  aria-label="Back"
                >
                  Done
                </ButtonLink>
              )}
            </Box>
          </Flex>
        </>
      ) : (
        <>
          <Flex
            css={{
              flexDirection: "column",
              gap: 40,
              maxW: "400px",
              [LANDSCAPE]: {
                flexDirection: "row",
                maxW: 1100,
                my: "auto",
                gap: "min(150px, 10vw)",
              },
            }}
          >
            <Box
              css={{
                spaceY: 16,
                [LANDSCAPE]: {
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 0 0px",
                },
              }}
            >
              <Box
                css={{
                  [LANDSCAPE]: {
                    flex: "1 0 0px",
                  },
                }}
              />

              <Flex
                css={{
                  flexDirection: "row",
                  items: "center",
                  gap: 16,
                  flexWrap: "wrap",
                  "@maxSm": {
                    mt: -16,
                  },
                  [LANDSCAPE]: { flexDirection: "column", flex: "1 0 0px" },
                }}
              >
                <Img
                  src={laurel}
                  css={{
                    maxW: 100,
                    "@xs": {
                      mx: "auto",
                      maxW: 100,
                    },
                    borderRadius: 6,
                    [LANDSCAPE]: {
                      maxW: 160,
                    },
                    "@maxSm": {
                      maxW: 70,
                    },
                  }}
                />
                <Heading
                  css={{
                    fontScale: "2xl",
                    lineHeight: 1.1,
                    color: colors.text,
                    [LANDSCAPE]: {
                      fontScale: "4xl",
                    },
                  }}
                >
                  Nominate a {showEapWording ? "Provider" : "Supplier"}
                </Heading>
              </Flex>
              <Text
                css={{
                  color: colors.text2,
                  fontScale: "sm",
                  lineHeight: "135%",
                  [LANDSCAPE]: {
                    fontScale: "md",
                  },
                }}
              >
                {showEapWording ? (
                  <>
                    Do you know someone who should join Givenwell's EAP to support our members? <br />
                    <br />
                    Send us their details, and we will do our due diligence to ensure they fit our criteria. If they do,
                    we will aim to get them onboarded soon so you can use your free sessions with them.
                  </>
                ) : (
                  <>
                    Know someone who should join the Givenwell marketplace?
                    <br />
                    <br />
                    Send us their details below, and we'll do our best to get them on board so you can start spending
                    your points on their awesome products and services!
                  </>
                )}
              </Text>
              <Box
                css={{
                  [LANDSCAPE]: {
                    flex: "2 0 0px",
                  },
                  "@maxSm": {
                    display: "none",
                  },
                }}
              />
            </Box>
            <Form
              css={{
                display: "flex",
                flexDirection: "column",
                [LANDSCAPE]: {
                  flex: "1 0 0px",
                },
              }}
              onSubmit={() => {
                captureCustomEvent("user_nominated_supplier", {});
                if (initialEmail) {
                  captureCustomEvent("member_nominated_supplier", { member_email: initialEmail });
                }
                mut.mutate({
                  request_message: details.trim(),
                  requester_email: email.trim() ? email.trim() : undefined,
                  utm: utm?.trim() ? utm.trim() : undefined,
                });
              }}
            >
              <Box css={{ spaceY: 24 }}>
                <Field css={{ gap: 0 }}>
                  <Label
                    css={{
                      mb: 4,
                      [LANDSCAPE]: {
                        fontScale: "md",
                      },
                    }}
                  >
                    Nominated {showEapWording ? "Provider" : "Supplier(s)"}
                  </Label>
                  <Description
                    css={{
                      mb: 12,
                      color: colors.text2,
                      [LANDSCAPE]: {
                        fontScale: "sm",
                      },
                      lineHeight: "135%",
                    }}
                  >
                    Who should we approach? Please share any details you have.{" "}
                    {showEapWording ? "" : "You can list more than one supplier at a time."}
                  </Description>
                  <Textarea
                    required
                    placeholder={
                      showEapWording
                        ? "Practitioner or business name. Phone number, website or email."
                        : "Business name, contact details, your reason for nominating"
                    }
                    value={details}
                    onValueChange={setDetails}
                    className="ph-clear"
                    rows={5}
                  />
                </Field>
                {showEmail ? (
                  <Field css={{ gap: 0 }}>
                    <Label
                      css={{
                        mb: 4,
                        [LANDSCAPE]: {
                          fontScale: "md",
                        },
                      }}
                    >
                      Your Email <Span css={{ opacity: 0.35, fontWeight: "normal" }}>(optional)</Span>
                    </Label>
                    <Description
                      css={{
                        mb: 12,
                        color: colors.text2,
                        [LANDSCAPE]: {
                          fontScale: "sm",
                        },
                        lineHeight: "135%",
                      }}
                    >
                      If you want to be notified when the {showEapWording ? "provider" : "supplier"} is onboarded,
                      please enter your email address. We’ll keep it private and won’t share it with the{" "}
                      {showEapWording ? "provider" : "supplier"}.
                    </Description>
                    <Input
                      type="email"
                      value={email}
                      onValueChange={setEmail}
                      placeholder="e.g. me@gmail.com"
                      css={{ input: { background: colors.white } }}
                    />
                    <Box
                      css={{
                        padding: 16,
                        borderRadius: 16,
                        backgroundColor: colors.warmGrey,
                        spaceY: 4,
                        fontScale: "xs",
                        [LANDSCAPE]: {
                          fontScale: "sm",
                        },
                      }}
                    ></Box>
                  </Field>
                ) : (
                  <Box>
                    <Flex css={{ flexDir: "column", mb: 16 }}>
                      <Label
                        css={{
                          mb: 4,
                          [LANDSCAPE]: {
                            fontScale: "md",
                          },
                        }}
                      >
                        Data Privacy 🤫
                      </Label>
                      <Text css={{ fontScale: "xs", color: colors.text2, lineHeight: "135%" }}>
                        Givenwell will keep your name and email address private. Your employer and the{" "}
                        {showEapWording ? "provider" : "supplier"} won't know the nomination came from you.
                      </Text>
                    </Flex>
                  </Box>
                )}
              </Box>
              <Button
                type="submit"
                variant="filled"
                color="primary"
                css={{
                  width: "100%",
                  "&:disabled": {
                    color: "white",
                    background: colors.navy,
                    opacity: 0.4,
                  },
                  [LANDSCAPE]: { maxW: 250, alignSelf: "left", height: 48 },
                }}
                disabled={!details.trim()}
                loading={mut.isPending}
              >
                Submit
              </Button>
            </Form>
          </Flex>
        </>
      )}
      <Box css={{ mt: "auto", pb: 40, pt: 48 }}>
        <Text css={{ fontScale: "sm", textAlign: "center", color: colors.text2 }}>
          If you have any questions, please email us at <br />
          <EmailLink href="mailto:suppliers@givenwell.com">suppliers@givenwell.com</EmailLink>
        </Text>
      </Box>
    </Box>
  );
}

const Ul = styled("ul", {
  pl: 16,
  mt: 4,
  listStyle: "disc",
});

const EmailLink = styled("a", {
  color: colors.blue500,
  textDecoration: "underline",
});
